import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Idea } from '@models/Sessions/Idea/Idea';
import { Participant } from '@models/Sessions/Participant';
import { Session } from '@models/Sessions/Session';
import { from } from 'rxjs';
import { map, mergeMap, reduce, switchMap } from 'rxjs/operators';

import { RequestService } from '../request.service';

export interface CollaboratorSuggestion {
  participant: Participant;
  session: Session;
  score: number;
  ideas: Idea[];
  keywords: string[];
  added?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ExFinderService {
  api = {
    root: '/exfinder',
    url: {
      root: '/url',
      collabSuggest: '/collab-suggest',
    },
  };

  constructor(
    private client: RequestService,
    private http: HttpClient,
  ) {}

  collabSuggestUrl(session: Session) {
    return this.client
      .get<any>({
        uri:
          this.api.root +
          this.api.url.root +
          this.api.url.collabSuggest +
          `/${session._id}`,
      })
      .pipe(map(response => response.url));
  }

  filterSuggestionsForSession(suggestions: any[], session: Session) {
    return suggestions.filter(
      suggestion =>
        suggestion[1][1].length > 0 &&
        suggestion[1][1][0].toString() === session._id,
    );
  }

  mapSuggestionsToParticipants(
    suggestions: any[],
    session: Session,
  ): CollaboratorSuggestion[] {
    return suggestions
      .map(suggestion => ({
        participant: session.config.participants.find(
          participant =>
            participant._id.toString() === suggestion[0].toString(),
        ),
        session,
        score: suggestion[1][0],
        ideas: suggestion[1][2]
          .map(id =>
            session.data.ideas.find(
              _idea => _idea._id.toString() === id.toString(),
            ),
          )
          .filter(_ => !!_)
          .filter(
            (_idea, index, list) =>
              list.findIndex(i => i._id.toString() === _idea._id.toString()) ===
              index,
          ),
        keywords: suggestion[1][3],
      }))
      .filter(suggestion => !!suggestion.participant);
  }

  getSuggestionsForText(session: Session, fulltext: string) {
    return this.collabSuggestUrl(session).pipe(
      switchMap(url => {
        return this.http.post<any[]>(url, {
          session: session._id,
          fulltext,
        });
      }),
    );
  }

  suggestCollaborators(session: Session, idea: Idea) {
    return this.getSuggestionsForText(
      session,
      idea.title + ' ' + idea.one_liner + ' ' + idea.description,
    )
      .pipe(
        map((suggestions: any[]) =>
          this.filterSuggestionsForSession(suggestions, session),
        ),
      )
      .pipe(
        map(suggestions =>
          this.mapSuggestionsToParticipants(suggestions, session)
            .filter(
              suggestion =>
                suggestion.participant._id !== session.me._id &&
                suggestion.participant._id.toString() !==
                  idea.created_by.toString() &&
                !idea.collaborators.some(
                  collaborator =>
                    collaborator.participant_id.toString() ===
                    suggestion.participant._id,
                ),
            )
            .slice(0, 3),
        ),
      );
  }

  pickRelevantParticipants(session: Session) {
    return this.getSuggestionsForText(
      session,
      session.config.title +
        ' ' +
        session.config.one_liner +
        ' ' +
        session.config.description,
    )
      .pipe(
        map((suggestions: any[]) =>
          this.filterSuggestionsForSession(suggestions, session),
        ),
      )
      .pipe(
        map((suggestions: any[]) =>
          this.mapSuggestionsToParticipants(suggestions, session).slice(0, 5),
        ),
      );
  }

  pickRelevantParticipantsForMultipleSessions(sessions: Session[]) {
    return from(sessions).pipe(
      mergeMap(session => this.pickRelevantParticipants(session)),
      reduce(
        (total, results) => [...total, ...results],
        [] as CollaboratorSuggestion[],
      ),
    );
  }
}
